// ─── Template type enum matching backend ─────────────────────────────────────

export const AiPromptTemplateType = {
  Reply: 0,
  Draft: 1,
  Summary: 2,
  FollowUp: 3,
  TaskAgent: 4,
} as const;

export type AiPromptTemplateTypeValue = (typeof AiPromptTemplateType)[keyof typeof AiPromptTemplateType];

export const AiPromptUseCase = {
  ReplyDefault: 'conversation.reply',
  ReplyMention: 'conversation.reply.mention',
  ReplyExternalUnmuted: 'conversation.reply.external.unmuted',
  ReplyInstagramComment: 'conversation.reply.instagram.comment',
  ReplyMessengerComment: 'conversation.reply.messenger.comment',
  ReplyWhatsAppGroup: 'conversation.reply.whatsapp.group',
  Draft: 'conversation.draft',
  Summary: 'conversation.summary',
  FollowUp: 'conversation.followup',
  TaskAgent: 'task.agent',
} as const;

export type AiExternalChannelKey = 'whatsapp' | 'telegram' | 'instagram' | 'messenger' | 'webchat' | 'tiktok' | 'email';

export type AiPromptUseCaseId = (typeof AiPromptUseCase)[keyof typeof AiPromptUseCase] | `${typeof AiPromptUseCase.ReplyExternalUnmuted}.${AiExternalChannelKey}`;

export const AI_EXTERNAL_CHANNEL_KEYS: AiExternalChannelKey[] = [
  'whatsapp',
  'telegram',
  'instagram',
  'messenger',
  'webchat',
  'tiktok',
  'email',
];

export const buildExternalUnmutedUseCaseId = (channelKey?: AiExternalChannelKey | 'all' | null): AiPromptUseCaseId => {
  if (!channelKey || channelKey === 'all') {
    return AiPromptUseCase.ReplyExternalUnmuted;
  }

  return `${AiPromptUseCase.ReplyExternalUnmuted}.${channelKey}` as AiPromptUseCaseId;
};

export const isExternalUnmutedUseCase = (useCaseId: string): boolean =>
  useCaseId === AiPromptUseCase.ReplyExternalUnmuted
  || useCaseId.startsWith(`${AiPromptUseCase.ReplyExternalUnmuted}.`);

export const isChannelSpecificExternalUnmutedUseCase = (useCaseId: string): boolean =>
  useCaseId.startsWith(`${AiPromptUseCase.ReplyExternalUnmuted}.`);

export const isCommentSurfaceUseCase = (useCaseId: string): boolean =>
  useCaseId === AiPromptUseCase.ReplyInstagramComment
  || useCaseId === AiPromptUseCase.ReplyMessengerComment;

export const isWhatsAppGroupUseCase = (useCaseId: string): boolean =>
  useCaseId === AiPromptUseCase.ReplyWhatsAppGroup;

export const isDedicatedReplySurfaceUseCase = (useCaseId: string): boolean =>
  isCommentSurfaceUseCase(useCaseId) || isWhatsAppGroupUseCase(useCaseId);

export const usesDedicatedPromptOverride = (useCaseId: string): boolean =>
  isChannelSpecificExternalUnmutedUseCase(useCaseId) || isDedicatedReplySurfaceUseCase(useCaseId);

export interface AiExternalChannelOverrideStatus {
  channelKey: string;
  useCaseId: string;
  isCustomized: boolean;
}

export type AiSettingsTabId = 'general' | AiPromptUseCaseId;

export const AI_SETTINGS_GENERAL_TAB = {
  id: 'general' as const,
  tabLabel: 'General',
  title: 'General Settings',
  description: 'Configure AI providers and workspace-wide assistant defaults.',
};

export type AiPromptToolPolicy = 'tenantAllowed' | 'disabled';

export interface AiPromptUseCaseTabConfig {
  id: AiPromptUseCaseId;
  tabLabel: string;
  title: string;
  description: string;
  templateType: AiPromptTemplateTypeValue;
  toolPolicy: AiPromptToolPolicy;
  templateScopeNote?: string;
}

export const AI_PROMPT_USE_CASE_TABS: AiPromptUseCaseTabConfig[] = [
  {
    id: AiPromptUseCase.ReplyDefault,
    tabLabel: 'Team chats',
    title: 'Replies in team chats',
    description: 'How the assistant answers in internal conversations with your team.',
    templateType: AiPromptTemplateType.Reply,
    toolPolicy: 'tenantAllowed',
  },
  {
    id: AiPromptUseCase.ReplyMention,
    tabLabel: 'When mentioned',
    title: 'Replies when the assistant is mentioned',
    description: 'Used when the latest message directly mentions the AI assistant.',
    templateType: AiPromptTemplateType.Reply,
    toolPolicy: 'disabled',
    templateScopeNote: 'Shares the same instructions as Team chats.',
  },
  {
    id: AiPromptUseCase.ReplyExternalUnmuted,
    tabLabel: 'Customer chats',
    title: 'Replies to customers',
    description: 'How the assistant answers customers on connected channels while auto-reply is on.',
    templateType: AiPromptTemplateType.Reply,
    toolPolicy: 'disabled',
    templateScopeNote: 'The All channels instructions are shared with Team chats until you save a channel-specific version.',
  },
  {
    id: AiPromptUseCase.Draft,
    tabLabel: 'Suggested replies',
    title: 'Suggested replies for your team',
    description: 'The assistant writes a draft that a teammate reviews and edits before sending.',
    templateType: AiPromptTemplateType.Draft,
    toolPolicy: 'tenantAllowed',
  },
  {
    id: AiPromptUseCase.Summary,
    tabLabel: 'Chat summaries',
    title: 'Conversation summaries',
    description: 'A short recap so your team can catch up on a conversation quickly.',
    templateType: AiPromptTemplateType.Summary,
    toolPolicy: 'tenantAllowed',
  },
  {
    id: AiPromptUseCase.FollowUp,
    tabLabel: 'Quiet-chat review',
    title: 'Review after a conversation goes quiet',
    description: 'Tools for the background review after a conversation goes quiet. Operations — which chats, the prompt, and the delay — are under Automations → Quiet chats. Turn the company switch on under Companies → AI Agent.',
    templateType: AiPromptTemplateType.FollowUp,
    toolPolicy: 'tenantAllowed',
  },
  {
    id: AiPromptUseCase.TaskAgent,
    tabLabel: 'Task agent',
    title: 'Tasks for the AI agent',
    description: 'Instructions and tools for background work on tasks assigned to the AI agent. Turn the company switch on under Companies → AI Agent.',
    templateType: AiPromptTemplateType.TaskAgent,
    toolPolicy: 'tenantAllowed',
  },
];

export const AI_INSTAGRAM_COMMENT_USE_CASE: AiPromptUseCaseTabConfig = {
  id: AiPromptUseCase.ReplyInstagramComment,
  tabLabel: 'Instagram comments',
  title: 'Replies to Instagram comments',
  description: 'Used under Instagram posts and live videos. Instagram DMs are handled by Customer chats.',
  templateType: AiPromptTemplateType.Reply,
  toolPolicy: 'tenantAllowed',
};

export const AI_MESSENGER_COMMENT_USE_CASE: AiPromptUseCaseTabConfig = {
  id: AiPromptUseCase.ReplyMessengerComment,
  tabLabel: 'Facebook comments',
  title: 'Replies to Facebook comments',
  description: 'Used under Facebook Page posts. Messenger DMs are handled by Customer chats.',
  templateType: AiPromptTemplateType.Reply,
  toolPolicy: 'tenantAllowed',
};

export const AI_WHATSAPP_GROUP_USE_CASE: AiPromptUseCaseTabConfig = {
  id: AiPromptUseCase.ReplyWhatsAppGroup,
  tabLabel: 'WhatsApp groups',
  title: 'Replies in WhatsApp groups',
  description: 'Used when someone mentions the assistant in a WhatsApp group. WhatsApp DMs are handled by Customer chats.',
  templateType: AiPromptTemplateType.Reply,
  toolPolicy: 'tenantAllowed',
};

export type ExternalChannelRailItemId = 'all' | AiExternalChannelKey | 'instagram-comments' | 'messenger-comments' | 'whatsapp-groups';

export type ExternalChannelRailItem = {
  id: ExternalChannelRailItemId;
  useCaseId: AiPromptUseCaseId;
  iconKind: 'whatsApp' | 'telegram' | 'instagram' | 'messenger' | 'webchat' | 'tiktok' | 'email' | null;
  nested?: boolean;
  inheritsFromAllChannels: boolean;
};

export const AI_EXTERNAL_CHANNEL_RAIL_ITEMS: ExternalChannelRailItem[] = [
  {
    id: 'all',
    useCaseId: AiPromptUseCase.ReplyExternalUnmuted,
    iconKind: null,
    inheritsFromAllChannels: false,
  },
  {
    id: 'whatsapp',
    useCaseId: buildExternalUnmutedUseCaseId('whatsapp'),
    iconKind: 'whatsApp',
    inheritsFromAllChannels: true,
  },
  {
    id: 'whatsapp-groups',
    useCaseId: AiPromptUseCase.ReplyWhatsAppGroup,
    iconKind: 'whatsApp',
    nested: true,
    inheritsFromAllChannels: false,
  },
  {
    id: 'telegram',
    useCaseId: buildExternalUnmutedUseCaseId('telegram'),
    iconKind: 'telegram',
    inheritsFromAllChannels: true,
  },
  {
    id: 'instagram',
    useCaseId: buildExternalUnmutedUseCaseId('instagram'),
    iconKind: 'instagram',
    inheritsFromAllChannels: true,
  },
  {
    id: 'instagram-comments',
    useCaseId: AiPromptUseCase.ReplyInstagramComment,
    iconKind: 'instagram',
    nested: true,
    inheritsFromAllChannels: false,
  },
  {
    id: 'messenger',
    useCaseId: buildExternalUnmutedUseCaseId('messenger'),
    iconKind: 'messenger',
    inheritsFromAllChannels: true,
  },
  {
    id: 'messenger-comments',
    useCaseId: AiPromptUseCase.ReplyMessengerComment,
    iconKind: 'messenger',
    nested: true,
    inheritsFromAllChannels: false,
  },
  {
    id: 'webchat',
    useCaseId: buildExternalUnmutedUseCaseId('webchat'),
    iconKind: 'webchat',
    inheritsFromAllChannels: true,
  },
  {
    id: 'tiktok',
    useCaseId: buildExternalUnmutedUseCaseId('tiktok'),
    iconKind: 'tiktok',
    inheritsFromAllChannels: true,
  },
  {
    id: 'email',
    useCaseId: buildExternalUnmutedUseCaseId('email'),
    iconKind: 'email',
    inheritsFromAllChannels: true,
  },
];

const AI_SETTINGS_TAB_IDS = new Set<string>([
  AI_SETTINGS_GENERAL_TAB.id,
  ...AI_PROMPT_USE_CASE_TABS.map((tab) => tab.id),
]);

export const parseAiSettingsTabParam = (value: string | null): AiSettingsTabId | null => {
  if (!value || !AI_SETTINGS_TAB_IDS.has(value)) {
    return null;
  }

  return value as AiSettingsTabId;
};

export const parseAiSettingsChannelParam = (value: string | null): ExternalChannelRailItemId | null => {
  if (!value || !AI_EXTERNAL_CHANNEL_RAIL_ITEMS.some((item) => item.id === value)) {
    return null;
  }

  return value as ExternalChannelRailItemId;
};

export const buildAiSettingsSearchParams = (
  current: URLSearchParams,
  selection: { tab: AiSettingsTabId; channel?: ExternalChannelRailItemId | 'all' | null },
): URLSearchParams => {
  const next = new URLSearchParams(current);
  if (selection.tab === AI_SETTINGS_GENERAL_TAB.id) {
    next.delete('tab');
  } else {
    next.set('tab', selection.tab);
  }

  if (selection.tab !== AiPromptUseCase.ReplyExternalUnmuted) {
    next.delete('channel');
  } else if (selection.channel === null || selection.channel === 'all') {
    next.delete('channel');
  } else if (selection.channel) {
    next.set('channel', selection.channel);
  }

  return next;
};

export const AI_PROMPT_TEMPLATE_TYPE_LABELS: Record<AiPromptTemplateTypeValue, string> = {
  [AiPromptTemplateType.Reply]: 'Customer Auto-Reply',
  [AiPromptTemplateType.Draft]: 'Employee Draft',
  [AiPromptTemplateType.Summary]: 'Conversation Summary',
  [AiPromptTemplateType.FollowUp]: 'Quiet-Chat Review',
  [AiPromptTemplateType.TaskAgent]: 'Task agent',
};

export const AI_PROMPT_TEMPLATE_DESCRIPTIONS: Record<AiPromptTemplateTypeValue, string> = {
  [AiPromptTemplateType.Reply]: 'Automatically replies to customer messages. This is what customers receive.',
  [AiPromptTemplateType.Draft]: 'Generates suggested replies for employees to review and edit before sending.',
  [AiPromptTemplateType.Summary]: 'Creates a bullet-point summary of the conversation for employees.',
  [AiPromptTemplateType.FollowUp]: 'Runs once after a conversation goes quiet. The prompt defines the operation; the run acts only through the tools selected for this use case.',
  [AiPromptTemplateType.TaskAgent]: 'Works on a task assigned to the AI agent. The run acts only through the tools selected for this use case.',
};

// ─── API Response & Request types ────────────────────────────────────────────

export interface AiPromptConfig {
  templateType: AiPromptTemplateTypeValue;
  systemPrompt: string | null;
  toneOfVoice: string | null;
  includeCompanyKnowledge: boolean;
  includeConversationHistory: boolean;
  conversationHistoryMessageCount: number | null;
  conversationHistoryTodayOnly: boolean;
  maxTokensResponse: number | null;
  customFields: string | null;
  version: number;
  isDefault: boolean;
  isInherited?: boolean;
  inheritsFromUseCaseId?: string | null;
  useCaseId?: string | null;
  availableVariables: AiPromptTemplateVariable[];
}

export interface AiPromptTemplateVariable {
  name: string;
  description: string;
  example: string;
}

export interface AiPromptVersion {
  version: number;
  systemPrompt: string;
  toneOfVoice: string | null;
  includeCompanyKnowledge: boolean;
  includeConversationHistory: boolean;
  conversationHistoryMessageCount: number | null;
  conversationHistoryTodayOnly: boolean;
  maxTokensResponse: number | null;
  customFields: string | null;
  changedAt: string;
  changeReason: string | null;
}

export interface UpdateAiPromptConfigRequest {
  systemPrompt: string;
  toneOfVoice: string | null;
  includeCompanyKnowledge: boolean;
  includeConversationHistory: boolean;
  conversationHistoryMessageCount: number | null;
  conversationHistoryTodayOnly: boolean;
  maxTokensResponse: number | null;
  customFields: string | null;
  changeReason: string | null;
}

export interface AiKernelFunctionToolPlugin {
  pluginId: string;
  displayName: string;
  kernelPluginName: string;
  isPublished: boolean;
}

export interface AiKernelFunctionSetting {
  functionId: string;
  functionDisplayName: string;
  pluginName: string;
  accessMode: 'read' | 'write';
  enabled: boolean;
  instruction: string | null;
  supportsInstruction: boolean;
  includeResultsInHistory: boolean;
  supportsHistoryInclusion: boolean;
  toolPlugins: AiKernelFunctionToolPlugin[];
}

export const AI_KERNEL_PLUGIN_INSTRUCTION_MAX_LENGTH = 4000;

export interface AiKernelPluginInstruction {
  functionId: string;
  instruction: string | null;
}

export interface AiToolHistoryInclusion {
  functionId: string;
  includeResultsInHistory: boolean;
}

export interface AiKernelFunctionPolicy {
  useCaseId: string;
  isConfigured: boolean;
  functions: AiKernelFunctionSetting[];
  toolPlugins: AiKernelFunctionToolPlugin[];
}

export interface AiAutoFunctionInvocationPolicy {
  useCaseId: string;
  isConfigured: boolean;
  maxToolCalls: number;
}

export interface AiResourceListPrefetchPreference {
  useCaseId: string;
  isConfigured: boolean;
  enabled: boolean;
}

export type AiChatProviderOption = 'Auto' | 'AzureOpenAI' | 'OpenAI' | 'DeepSeek' | 'Anthropic' | 'GoogleGemini' | 'MistralAI';

export interface AiProviderSetting {
  id: AiChatProviderOption | 'AzureOpenAI' | 'OpenAI' | 'DeepSeek' | 'Anthropic' | 'GoogleGemini' | 'MistralAI';
  displayName: string;
  isConfiguredAtDeployment: boolean;
  isTenantConfigured: boolean;
  isConfigured: boolean;
  enabled: boolean;
  effectiveEnabled: boolean;
  model: string | null;
  endpoint: string | null;
}

export interface AiProviderSettings {
  isConfigured: boolean;
  chatProvider: AiChatProviderOption;
  activeChatProvider: string;
  activeChatModelId: string;
  providers: AiProviderSetting[];
}

export interface UpdateAiProviderSettingsRequest {
  chatProvider: AiChatProviderOption;
  azureOpenAiEnabled: boolean;
  openAiEnabled: boolean;
  deepSeekEnabled: boolean;
  anthropicEnabled: boolean;
  googleGeminiEnabled: boolean;
  mistralAiEnabled: boolean;
}

export type AiProviderId = 'AzureOpenAI' | 'OpenAI' | 'DeepSeek' | 'Anthropic' | 'GoogleGemini' | 'MistralAI';

export interface AiProviderConnectionTestResult {
  success: boolean;
  provider: string;
  model: string;
  error: string | null;
}

export interface AiChatFailure {
  occurredAt: string;
  model: string;
  message: string;
}

export interface AiChatHealth {
  lastFailure: AiChatFailure | null;
}

export interface AiAzureOpenAiConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  endpoint: string;
  chatDeploymentName: string;
  embeddingDeploymentName: string;
  audioTranscriptionDeploymentName: string;
  apiVersion: string;
}

export interface UpdateAiAzureOpenAiConfigurationRequest {
  endpoint?: string;
  apiKey?: string;
  chatDeploymentName?: string;
  embeddingDeploymentName?: string;
  audioTranscriptionDeploymentName?: string;
  apiVersion?: string;
}

/** Empty means auto: reasoning stays at the model default until tools force an opt-out. */
export type AiReasoningEffort = '' | 'none' | 'minimal' | 'low' | 'medium' | 'high';

export const AI_REASONING_EFFORT_OPTIONS: AiReasoningEffort[] = ['', 'none', 'minimal', 'low', 'medium', 'high'];

export interface AiOpenAiConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  model: string;
  embeddingModel: string;
  temperature: number;
  maxOutputTokens: number;
  reasoningEffort: AiReasoningEffort;
}

export interface UpdateAiOpenAiConfigurationRequest {
  apiKey?: string;
  model?: string;
  embeddingModel?: string;
  temperature?: number;
  maxOutputTokens?: number;
  reasoningEffort?: AiReasoningEffort;
}

export interface AiDeepSeekConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  endpoint: string;
  model: string;
  thinkingEnabled: boolean;
}

export interface UpdateAiDeepSeekConfigurationRequest {
  apiKey?: string;
  endpoint?: string;
  model?: string;
  thinkingEnabled?: boolean;
}

export interface AiAnthropicConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  model: string;
  maxOutputTokens: number;
}

export interface UpdateAiAnthropicConfigurationRequest {
  apiKey?: string;
  model?: string;
  maxOutputTokens?: number;
}

export interface AiGoogleGeminiConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  model: string;
}

export interface UpdateAiGoogleGeminiConfigurationRequest {
  apiKey?: string;
  model?: string;
}

export interface AiMistralAiConfiguration {
  isTenantConfigured: boolean;
  hasApiKey: boolean;
  model: string;
}

export interface UpdateAiMistralAiConfigurationRequest {
  apiKey?: string;
  model?: string;
}

export type AiProviderConfiguration =
  | AiAzureOpenAiConfiguration
  | AiOpenAiConfiguration
  | AiDeepSeekConfiguration
  | AiAnthropicConfiguration
  | AiGoogleGeminiConfiguration
  | AiMistralAiConfiguration;

export type UpdateAiProviderConfigurationRequest =
  | UpdateAiAzureOpenAiConfigurationRequest
  | UpdateAiOpenAiConfigurationRequest
  | UpdateAiDeepSeekConfigurationRequest
  | UpdateAiAnthropicConfigurationRequest
  | UpdateAiGoogleGeminiConfigurationRequest
  | UpdateAiMistralAiConfigurationRequest;

export const AI_CHAT_PROVIDER_OPTIONS: AiChatProviderOption[] = [
  'Auto',
  'AzureOpenAI',
  'OpenAI',
  'DeepSeek',
  'Anthropic',
  'GoogleGemini',
  'MistralAI',
];

// ─── Tone of voice options ────────────────────────────────────────────────────

export interface ToneOption {
  value: string;
  label: string;
}

export const TONE_OPTIONS: ToneOption[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly & Approachable' },
  { value: 'empathetic', label: 'Empathetic & Caring' },
  { value: 'technical', label: 'Technical & Precise' },
  { value: 'concise', label: 'Concise & Direct' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
];

export interface FollowUpAutomation {
  id: string;
  name: string;
  isEnabled: boolean;
  assignedUseCaseIds: string[];
  triggerKind: string;
  delayMinutes: number | null;
  systemPrompt: string;
  allowedFunctionIds: string[];
  updatedAt: string;
}

export interface UpsertFollowUpAutomationRequest {
  name: string;
  isEnabled: boolean;
  assignedUseCaseIds: string[];
  triggerKind: string;
  delayMinutes: number | null;
  systemPrompt: string;
  allowedFunctionIds: string[];
}
